/**
 * Touch gesture support for viewports — pinch-to-zoom and two-finger-pan.
 * Cornerstone's tools handle single-touch, but multi-touch needs manual wiring.
 */
import { getRenderingEngine } from '@cornerstonejs/core';
import { RENDERING_ENGINE_ID } from '../cornerstone/init';

interface TouchState {
  lastDist: number;
  lastCenter: { x: number; y: number };
  active: boolean;
}

const states = new Map<HTMLElement, TouchState>();

function getDistance(t1: Touch, t2: Touch): number {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
}

function getCenter(t1: Touch, t2: Touch): { x: number; y: number } {
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  };
}

function onTouchStart(e: TouchEvent) {
  if (e.touches.length !== 2) return;
  e.preventDefault();

  const el = e.currentTarget as HTMLElement;
  states.set(el, {
    lastDist: getDistance(e.touches[0], e.touches[1]),
    lastCenter: getCenter(e.touches[0], e.touches[1]),
    active: true,
  });
}

function onTouchMove(e: TouchEvent) {
  if (e.touches.length !== 2) return;

  const el = e.currentTarget as HTMLElement;
  const state = states.get(el);
  if (!state?.active) return;

  e.preventDefault();

  const vpId = el.id;
  const engine = getRenderingEngine(RENDERING_ENGINE_ID);
  const vp = engine?.getViewport(vpId) as any;
  if (!vp) return;

  const newDist = getDistance(e.touches[0], e.touches[1]);
  const newCenter = getCenter(e.touches[0], e.touches[1]);

  // Pinch zoom
  const scale = newDist / state.lastDist;
  if (Math.abs(scale - 1) > 0.01) {
    const currentZoom = vp.getZoom?.() ?? 1;
    vp.setZoom?.(currentZoom * scale);
  }

  // Two-finger pan
  const dx = newCenter.x - state.lastCenter.x;
  const dy = newCenter.y - state.lastCenter.y;
  if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
    vp.setPan?.([
      (vp.getPan?.()[0] ?? 0) + dx,
      (vp.getPan?.()[1] ?? 0) + dy,
    ]);
  }

  vp.render?.();

  state.lastDist = newDist;
  state.lastCenter = newCenter;
}

function onTouchEnd(e: TouchEvent) {
  const el = e.currentTarget as HTMLElement;
  const state = states.get(el);
  if (state) state.active = false;
}

/** Attach touch gesture handlers to a viewport element */
export function enableTouchGestures(element: HTMLElement) {
  element.addEventListener('touchstart', onTouchStart, { passive: false });
  element.addEventListener('touchmove', onTouchMove, { passive: false });
  element.addEventListener('touchend', onTouchEnd);
  element.addEventListener('touchcancel', onTouchEnd);
}

/** Remove touch gesture handlers */
export function disableTouchGestures(element: HTMLElement) {
  element.removeEventListener('touchstart', onTouchStart);
  element.removeEventListener('touchmove', onTouchMove);
  element.removeEventListener('touchend', onTouchEnd);
  element.removeEventListener('touchcancel', onTouchEnd);
  states.delete(element);
}
